/**
 * GroupedLineItems - Shift-grouped line items display
 * 
 * EF-QUOTE-STRUCTURE-V1: Redesigned for professional client-ready structure
 * EF-QUOTE-UX-REFINEMENT-LOCK-V3: Updated format and column headers
 * EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Deterministic button behaviour
 * 
 * Features:
 * - Summary/Detailed view toggle
 * - 5-column structure: DESCRIPTION | QTY | UNITS | UNIT COST | TOTAL
 * - Shift format: "SHIFT NAME — Day DD-MM-YYYY HH:MM — Crew"
 * - Indented associated items (Travel, Meals, Supplements)
 * - Expand/collapse per section
 * - Matches PDF structure exactly
 * 
 * Button Behaviour (EF-QUOTE-FORM-AND-TOGGLE-FIX-V1):
 * - Summary: collapsed view, associated costs collapsed by default
 * - Detailed: all line items expanded, no need to click [+]
 * - Expand All: expands ALL shifts, all associated costs visible
 * - Collapse All: collapses ALL shifts, only top-level lines visible
 * - Per-shift +/- toggle overrides global state for that shift only
 */
import { useState, useMemo, useEffect } from "react";
import { ChevronDown, ChevronRight, Eye, EyeOff } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
// EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1
import { TaxDecisionAuditPopover } from "./TaxDecisionAuditPopover";
import { HelpPopover } from "../shared/HelpPopover";

/**
 * Group flat line items by shift_id, separating primary (labour) from supplements
 */
function groupLinesByShift(lines) {
  if (!lines || lines.length === 0) return [];
  
  const shiftGroups = {};
  
  for (const line of lines) {
    const shiftId = line.shift_id || "unknown";
    
    if (!shiftGroups[shiftId]) {
      shiftGroups[shiftId] = {
        shift_id: shiftId,
        primary: null,
        supplements: [],
        shift_name: line.shift_name || null,
        shift_date: line.shift_date || null,
        venue_name: line.venue_name || null,
      };
    }
    
    const lineType = line.line_type || "item";
    
    if (lineType === "labour") {
      if (!shiftGroups[shiftId].primary) {
        shiftGroups[shiftId].primary = line;
      } else {
        shiftGroups[shiftId].supplements.push(line);
      }
    } else {
      shiftGroups[shiftId].supplements.push(line);
    }
  }
  
  // Convert to array and calculate totals
  const shifts = Object.values(shiftGroups).map(group => {
    const primaryTotal = group.primary?.line_total || 0;
    const supplementsTotal = group.supplements.reduce((sum, l) => sum + (l.line_total || 0), 0);
    
    return {
      ...group,
      primary_total: primaryTotal,
      supplements_total: supplementsTotal,
      shift_total: primaryTotal + supplementsTotal,
    };
  });
  
  // Sort by date
  return shifts.sort((a, b) => {
    const dateA = a.primary?.description?.match(/\d{4}-\d{2}-\d{2}/)?.[0] || "";
    const dateB = b.primary?.description?.match(/\d{4}-\d{2}-\d{2}/)?.[0] || "";
    return dateA.localeCompare(dateB);
  });
}

/**
 * Extract shift name from description
 * Format: "{shift_name} ({date}) - Crew" → returns shift_name
 */
function extractShiftName(description) {
  if (!description) return "Shift";
  
  // Match: "Setup in San Remo (2026-02-24) - Crew"
  const match = description.match(/^(.+?)\s*\(\d{4}-\d{2}-\d{2}\)/);
  if (match) return match[1].trim();
  
  // Fallback: take before " - " or " — "
  const parts = description.split(/\s*[—-]\s*/);
  return parts[0] || "Shift";
}

/**
 * EF-QUOTE-UX-REFINEMENT-LOCK-V3: Format shift description with date and time
 * EVENTSFLO-QUOTE-HEADER-TAX-RENDERER-INTEGRITY-FIX-V2: derive the role suffix
 * from the line's actual description instead of hardcoding "Crew" — so
 * "Crew exVAT" / "General Production Crew" etc. render correctly.
 * Format: "{SHIFT_NAME} — {Day} {DD-MM-YYYY} {HH:MM} — {ROLE}"
 * Example: "MONTAGE — Mon 13-05-2026 08:00 — Crew exVAT"
 */
function extractRoleName(description) {
  if (!description) return "Crew";
  // Backend line description format:
  //   "{shift_name} ({date}) - {role_name}"
  // We want the trailing role name. Split by " - " or " — " and take
  // the last non-empty segment. Falls back to "Crew" if no separator.
  const parts = description.split(/\s+[-—]\s+/);
  const last = (parts[parts.length - 1] || "").trim();
  return last || "Crew";
}

function formatShiftDescription(line, shiftName) {
  // Try to get shift date and time from line metadata
  const shiftDate = line?.shift_date;
  const startTime = line?.start_time;
  const roleName = extractRoleName(line?.description);
  
  if (shiftDate) {
    // Parse the date
    const date = new Date(shiftDate);
    if (!isNaN(date.getTime())) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const day = dayNames[date.getDay()];
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      
      let dateTimeStr = `${day} ${dd}-${mm}-${yyyy}`;
      
      // Add time if available
      if (startTime) {
        dateTimeStr += ` ${startTime}`;
      }
      
      return `${shiftName} — ${dateTimeStr} — ${roleName}`;
    }
  }
  
  // Fallback to simple format
  return `${shiftName} — ${roleName}`;
}

/**
 * Format line item for display
 * Returns: { qty, multiplier, unitCost, total, qtyLabel, multiplierLabel }
 * 
 * EF-QUOTE-UI-AND-PDF-LOCK-V2: Uses stored qty/multiplier from backend.
 * For labour lines: qty = crew count, multiplier = hours
 * Display: "8 Crew × 8 Hours × €36.75"
 */
function formatLineItemDisplay(line) {
  // Use stored qty/multiplier from backend (EF-SHIFT-RULES-NORMALISATION-V2)
  // These should now be populated by the backend
  const qty = line.qty ?? null;
  const multiplier = line.multiplier ?? null;
  const unitCost = line.unit_price ?? line.unit_rate ?? 0;
  const total = line.line_total ?? 0;
  
  // Determine labels based on line type
  const lineType = line.line_type || "item";
  const description = (line.description || "").toLowerCase();
  
  let qtyLabel = "";
  let multiplierLabel = "";
  let displayQty = qty;
  let displayMultiplier = multiplier;
  
  if (lineType === "labour" || description.includes("crew")) {
    qtyLabel = "Crew";
    multiplierLabel = (displayMultiplier && displayMultiplier > 1) ? "Hours" : "Hour";
    
    // Fallback: If no stored qty/multiplier but have quantity, it's the aggregated value
    // This is a legacy compatibility check
    if (displayQty === null && displayMultiplier === null && line.quantity) {
      // Cannot properly decompose aggregated quantity, so show as-is with warning
      displayQty = line.quantity;
      displayMultiplier = null;
    }
  } else if (description.includes("meal") || description.includes("repas")) {
    qtyLabel = "Crew";
    multiplierLabel = "";
    if (displayQty === null) displayQty = line.quantity ?? 1;
  } else if (description.includes("km") || description.includes("distance") || description.includes("travel")) {
    qtyLabel = qty === 1 ? "Car" : "Cars";
    multiplierLabel = "km";
    if (displayQty === null) displayQty = line.quantity ?? 1;
  } else if (description.includes("toll")) {
    qtyLabel = qty === 1 ? "Car" : "Cars";
    multiplierLabel = "";
    if (displayQty === null) displayQty = line.quantity ?? 1;
  } else {
    qtyLabel = "";
    multiplierLabel = "";
    if (displayQty === null) displayQty = line.quantity ?? 1;
  }
  
  return { 
    qty: displayQty ?? 1, 
    multiplier: displayMultiplier, 
    unitCost, 
    total, 
    qtyLabel, 
    multiplierLabel 
  };
}

export function GroupedLineItems({ 
  lines = [], 
  shiftGroups: providedShiftGroups = null, // EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1
  formatCurrency,
  testIdPrefix = "quote-line",
  defaultView = "summary" // "summary" or "detailed"
}) {
  /**
   * EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Deterministic state management
   * 
   * State source:
   * - viewMode: "summary" | "detailed"
   * - expandedShifts: { [shift_id]: true/false }
   * 
   * Logic:
   * - Summary: viewMode="summary", expandedShifts={}
   * - Detailed: viewMode="detailed", expandedShifts=ALL TRUE
   * - Expand All: expandedShifts=ALL TRUE
   * - Collapse All: expandedShifts=ALL FALSE
   * - Per-shift toggle overrides global state for that shift only
   */
  const [viewMode, setViewMode] = useState(defaultView);
  const [expandedShifts, setExpandedShifts] = useState({});
  
  // EF-QUOTE-SINGLE-SOURCE-OF-TRUTH-LOCK-V1: Use provided shiftGroups from view model, or group from lines
  const shiftGroups = useMemo(() => {
    if (providedShiftGroups) {
      return providedShiftGroups;
    }
    return groupLinesByShift(lines);
  }, [providedShiftGroups, lines]);
  
  // EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Generate all expanded state helper
  const getAllExpandedState = () => {
    const allExpanded = {};
    shiftGroups.forEach(shift => {
      allExpanded[shift.shift_id] = true;
    });
    return allExpanded;
  };
  
  const getAllCollapsedState = () => {
    const allCollapsed = {};
    shiftGroups.forEach(shift => {
      allCollapsed[shift.shift_id] = false;
    });
    return allCollapsed;
  };
  
  // EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Per-shift toggle - overrides global state for that shift only
  const toggleShiftExpanded = (shiftId) => {
    setExpandedShifts(prev => ({
      ...prev,
      [shiftId]: !prev[shiftId]
    }));
  };
  
  // EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Summary button - collapsed view
  const handleSummaryClick = () => {
    setViewMode("summary");
    setExpandedShifts({});
  };
  
  // EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Detailed button - all expanded
  const handleDetailedClick = () => {
    setViewMode("detailed");
    setExpandedShifts(getAllExpandedState());
  };
  
  // EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Expand All button
  const expandAll = () => {
    setExpandedShifts(getAllExpandedState());
  };
  
  // EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Collapse All button
  const collapseAll = () => {
    setExpandedShifts(getAllCollapsedState());
  };
  
  if (shiftGroups.length === 0) {
    return (
      <div className="p-6">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
          Line Items
        </h2>
        <div className="py-8 text-center text-muted-foreground italic">
          No line items
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-2" data-testid="grouped-line-items">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
          Line Items
          {/* EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1 */}
          <HelpPopover
            testId={
              testIdPrefix?.includes("invoice")
                ? "invoice-line-tax-audit-help"
                : "quote-line-tax-audit-help"
            }
            title="Line-level tax audit"
          >
            <p>
              Click any VAT tag next to a line to see the tax decision that
              was snapshotted when this document was generated.
            </p>
            <p>
              Snapshots do <strong>not</strong> re-evaluate against the current
              Tax Rules Matrix — so historically issued quotes and invoices stay
              stable even if rules are edited later.
            </p>
            <p>
              A <strong>Manual review</strong> badge means finance / admin
              should double-check that line before the document is issued.
            </p>
          </HelpPopover>
        </h2>
        
        <div className="flex items-center gap-2 print:hidden">
          {/* Summary/Detailed Toggle - EF-QUOTE-FORM-AND-TOGGLE-FIX-V1 */}
          <div className="flex items-center gap-1 border rounded-md p-0.5 bg-surface-elevated/50">
            <Button
              variant={viewMode === "summary" ? "default" : "ghost"}
              size="sm"
              onClick={handleSummaryClick}
              className="h-6 px-2 text-xs"
              data-testid="view-mode-summary"
            >
              <EyeOff className="h-3 w-3 mr-1" />
              Summary
            </Button>
            <Button
              variant={viewMode === "detailed" ? "default" : "ghost"}
              size="sm"
              onClick={handleDetailedClick}
              className="h-6 px-2 text-xs"
              data-testid="view-mode-detailed"
            >
              <Eye className="h-3 w-3 mr-1" />
              Detailed
            </Button>
          </div>
          
          {/* Expand/Collapse All - EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Available in both views */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={expandAll}
              className="h-6 px-2 text-xs text-muted-foreground"
              data-testid="expand-all-btn"
            >
              Expand All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={collapseAll}
              className="h-6 px-2 text-xs text-muted-foreground"
              data-testid="collapse-all-btn"
            >
              Collapse All
            </Button>
          </div>
        </div>
      </div>
      
      {/* EF-QUOTE-UX-REFINEMENT-LOCK-V3: 5-Column Table Header with adjusted widths */}
      {/* Description gets 60% width, numeric columns are smaller and right-aligned */}
      <div className="grid grid-cols-12 gap-1 px-3 py-2 border-b-2 border-border text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-surface-elevated/30">
        <div className="col-span-6">Description</div>
        <div className="col-span-1 text-right">Qty</div>
        <div className="col-span-2 text-right">Units</div>
        <div className="col-span-1 text-right">Unit</div>
        <div className="col-span-2 text-right">Total</div>
      </div>
      
      {/* Shift Groups */}
      {shiftGroups.map((shift, shiftIndex) => {
        const shiftName = extractShiftName(shift.primary?.description) || shift.shift_name || "Shift";
        // EF-QUOTE-FORM-AND-TOGGLE-FIX-V1: Check expanded state from single source
        const isExpanded = expandedShifts[shift.shift_id] === true;
        const showSupplements = isExpanded;
        
        // EF-QUOTE-UX-REFINEMENT-LOCK-V3: Format shift description with date/time
        const shiftDescription = formatShiftDescription(shift.primary || shift, shiftName);
        
        return (
          <div 
            key={shift.shift_id || shiftIndex} 
            className="border-b border-border/50 last:border-b-0"
            data-testid={`${testIdPrefix}-shift-${shiftIndex}`}
          >
            {/* Primary Crew Line: "SHIFT NAME — Day DD-MM-YYYY HH:MM — Crew" */}
            {shift.primary && (
              <div 
                className={cn(
                  "grid grid-cols-12 gap-1 px-3 py-2 bg-surface-elevated/50",
                  shift.supplements.length > 0 && "hover:bg-surface-elevated/70 cursor-pointer"
                )}
                onClick={() => shift.supplements.length > 0 && toggleShiftExpanded(shift.shift_id)}
                data-testid={`${testIdPrefix}-${shiftIndex}-primary`}
                title={shift.primary.task_definition_description || shift.primary.description || shift.primary.name || shiftDescription}
              >
                <div className="col-span-6 flex items-center gap-2">
                  {shift.supplements.length > 0 && (
                    <span className="text-muted-foreground">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-foreground">
                    {shiftDescription}
                  </span>
                  {/* EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1 */}
                  <span onClick={(e) => e.stopPropagation()}>
                    <TaxDecisionAuditPopover
                      line={shift.primary}
                      lineId={`${testIdPrefix}-${shiftIndex}-primary`}
                    />
                  </span>
                </div>
                
                {(() => {
                  const display = formatLineItemDisplay(shift.primary);
                  return (
                    <>
                      <div className="col-span-1 text-sm text-right font-mono text-foreground">
                        {display.qty}
                      </div>
                      <div className="col-span-2 text-sm text-right font-mono text-foreground">
                        {display.multiplier ? `${display.multiplier}h` : "—"}
                      </div>
                      <div className="col-span-1 text-sm text-right font-mono text-foreground">
                        {formatCurrency(display.unitCost)}
                      </div>
                      <div className="col-span-2 text-sm text-right font-mono font-semibold text-foreground">
                        {formatCurrency(display.total)}
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            
            {/* Associated Costs - Collapsed View */}
            {!isExpanded && shift.supplements.length > 0 && (
              <div 
                className="grid grid-cols-12 gap-1 px-3 py-1.5 pl-9 border-l-2 border-primary/20 ml-3 bg-white cursor-pointer hover:bg-slate-50"
                onClick={() => toggleShiftExpanded(shift.shift_id)}
              >
                <div className="col-span-6 flex items-center gap-1 text-xs text-muted-foreground">
                  <span className="text-primary font-medium">[+]</span>
                  <span className="italic">Associated Costs ({shift.supplements.length})</span>
                </div>
                <div className="col-span-1"></div>
                <div className="col-span-2"></div>
                <div className="col-span-1"></div>
                <div className="col-span-2 text-xs text-right font-mono text-muted-foreground font-medium">
                  {formatCurrency(shift.supplements_total)}
                </div>
              </div>
            )}
            
            {/* Associated Costs - Detailed View (Expanded) */}
            {showSupplements && shift.supplements.length > 0 && (
              <div className="border-l-2 border-primary/20 ml-3">
                {/* Collapse header */}
                <div 
                  className="grid grid-cols-12 gap-1 px-3 py-1.5 pl-9 bg-slate-50 cursor-pointer hover:bg-slate-100"
                  onClick={() => toggleShiftExpanded(shift.shift_id)}
                >
                  <div className="col-span-6 flex items-center gap-1 text-xs text-muted-foreground">
                    <span className="text-primary font-medium">[−]</span>
                    <span className="italic">Associated Costs ({shift.supplements.length})</span>
                  </div>
                  <div className="col-span-6"></div>
                </div>
                
                {shift.supplements.map((line, lineIndex) => {
                  const display = formatLineItemDisplay(line);
                  // Extract simple description (remove date/time)
                  const simpleDesc = (line.description || "").replace(/\d{4}-\d{2}-\d{2}\s*:\s*/g, "").trim();
                  // EF-QUOTE-TASK-DESCRIPTION-VIEWMODEL-FIX-V1: Use task_definition_description as primary source
                  const tooltipText = line.task_definition_description || line.description || line.name || simpleDesc;
                  
                  return (
                    <div 
                      key={lineIndex}
                      className="grid grid-cols-12 gap-1 px-3 py-1.5 pl-9 hover:bg-surface-elevated/20"
                      data-testid={`${testIdPrefix}-${shiftIndex}-supplement-${lineIndex}`}
                      title={tooltipText}
                    >
                      <div className="col-span-6 text-xs text-muted-foreground">
                        → {simpleDesc}
                        {/* EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1 */}
                        <span onClick={(e) => e.stopPropagation()}>
                          <TaxDecisionAuditPopover
                            line={line}
                            lineId={`${testIdPrefix}-${shiftIndex}-supplement-${lineIndex}`}
                          />
                        </span>
                      </div>
                      <div className="col-span-1 text-xs text-right font-mono text-muted-foreground">
                        {display.qty}
                      </div>
                      <div className="col-span-2 text-xs text-right font-mono text-muted-foreground">
                        {display.multiplier ? `${display.multiplier}` : "—"}
                      </div>
                      <div className="col-span-1 text-xs text-right font-mono text-muted-foreground">
                        {formatCurrency(display.unitCost)}
                      </div>
                      <div className="col-span-2 text-xs text-right font-mono text-muted-foreground">
                        {formatCurrency(display.total)}
                      </div>
                    </div>
                  );
                })}
                
                {/* Associated Costs Subtotal */}
                <div className="grid grid-cols-12 gap-1 px-3 py-1.5 pl-9 border-t border-dashed border-border/50">
                  <div className="col-span-10 text-xs text-right text-muted-foreground italic">
                    Associated Costs Total:
                  </div>
                  <div className="col-span-2 text-xs text-right font-mono text-muted-foreground font-medium">
                    {formatCurrency(shift.supplements_total)}
                  </div>
                </div>
              </div>
            )}
            
            {/* Shift Subtotal */}
            <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-surface-elevated/20 border-t border-border/30">
              <div className="col-span-10 text-xs text-right font-medium text-foreground">
                Shift Total:
              </div>
              <div className="col-span-2 text-sm text-right font-mono font-semibold text-foreground">
                {formatCurrency(shift.shift_total)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default GroupedLineItems;
